import type { ReactNode } from "react";
import { z } from "zod";

/**
 * Dashboard model. A dashboard is plain JSON — an ordered list of widgets,
 * each bound to a named data source — so a layout can be saved, shared,
 * switched between, or generated by a script without touching React. What a
 * source *returns* is code (it may hold formatters and React nodes), which
 * is why sources are registered by the app and referenced by id from config.
 */

export type SourceKind = "metric" | "series" | "table" | "checklist";

export interface MetricData {
  /** Already formatted for display. */
  value: string;
  /** Muted line under the value, e.g. "8.4% this period". */
  detail?: string;
  trend?: "up" | "down" | "flat";
  /** Colours the trend: `true` good, `false` bad, `null`/absent neutral. */
  favorable?: boolean | null;
}

export interface SeriesData {
  /** Key in each point holding the x-axis label. */
  x: string;
  points: Array<Record<string, string | number>>;
  series: Array<{ key: string; label: string }>;
  /** Formats y-axis ticks and tooltips. */
  formatValue?: (value: number) => string;
}

export interface TableData {
  columns: Array<{
    key: string;
    label: string;
    align?: "start" | "end";
    /** Lets the viewer order rows by the matching entry in `row.sort`. */
    sortable?: boolean;
  }>;
  rows: Array<{
    id: string;
    cells: Record<string, ReactNode>;
    /** Plain values for sorting and filtering; cells can contain React nodes. */
    sort?: Record<string, string | number>;
  }>;
  /** Shows a search field that matches the plain values in `row.sort`. */
  filterable?: boolean;
  /** Shown instead of the table when there are no rows. */
  empty?: string;
  /** Optional link under the table, e.g. "All invoices". */
  footer?: { label: string; href: string };
}

export interface ChecklistData {
  steps: Array<{
    id: string;
    label: string;
    detail?: string;
    done: boolean;
    href?: string;
  }>;
  /** A first-run callout shown above the checklist. */
  banner?: { title: string; detail: string };
  /** Browser-local, workspace-specific key for hiding this checklist. */
  dismissKey?: string;
}

export interface DataByKind {
  metric: MetricData;
  series: SeriesData;
  table: TableData;
  checklist: ChecklistData;
}

export interface DataSource<K extends SourceKind = SourceKind> {
  id: string;
  /** Shown in the builder and as a widget's default title. */
  label: string;
  description?: string;
  kind: K;
  /**
   * When false, a saved layout can still load this source, but the builder
   * does not offer it for a new block.
   */
  offered?: boolean;
  load: () => Promise<DataByKind[K]>;
}

/** A source of any kind, narrowed by its `kind` discriminant. */
export type AnyDataSource = {
  [K in SourceKind]: DataSource<K>;
}[SourceKind];

export const SPANS = [3, 4, 6, 8, 12] as const;
export type Span = (typeof SPANS)[number];

export const SPAN_LABELS: Record<Span, string> = {
  3: "Quarter width",
  4: "Third width",
  6: "Half width",
  8: "Two-thirds width",
  12: "Full width",
};

export const widgetSchema = z.object({
  id: z.string().min(1),
  /** Widget type from the registry; defaults follow the source's kind. */
  type: z.string().min(1),
  /** Id of a registered data source. */
  source: z.string().min(1),
  title: z.string().optional(),
  span: z.union(SPANS.map((span) => z.literal(span)) as never),
  options: z.record(z.string(), z.unknown()).optional(),
});

export type WidgetConfig = Omit<z.infer<typeof widgetSchema>, "span"> & {
  span: Span;
};

export const presetSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  widgets: z.array(widgetSchema),
});

export type Preset = { id: string; name: string; widgets: WidgetConfig[] };

export const dashboardStateSchema = z.object({
  version: z.literal(1),
  activeId: z.string(),
  presets: z.array(presetSchema).min(1),
});

export interface DashboardState {
  version: 1;
  activeId: string;
  presets: Preset[];
}

export function newId(prefix: string): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `${prefix}-${random}`;
}

/** The built-in layouts, as state. */
export function stateFromDefaults(
  defaults: Preset[],
  activeId?: string,
): DashboardState {
  const presets = defaults.map((preset) => ({
    ...preset,
    widgets: preset.widgets.map((widget) => ({ ...widget })),
  }));
  const first = presets[0];
  if (!first) throw new Error("A dashboard needs at least one default preset");
  return {
    version: 1,
    activeId: presets.some((p) => p.id === activeId) ? activeId! : first.id,
    presets,
  };
}

/**
 * Validates whatever storage returned. Anything unreadable or malformed
 * falls back to the defaults rather than throwing — a corrupted saved layout
 * must never take the page down with it.
 */
export function normalizeState(
  raw: unknown,
  defaults: Preset[],
): DashboardState {
  const parsed = dashboardStateSchema.safeParse(raw);
  if (!parsed.success) return stateFromDefaults(defaults);
  const state = parsed.data as DashboardState;
  const activeId = state.presets.some((p) => p.id === state.activeId)
    ? state.activeId
    : (state.presets[0]?.id ?? "");
  return { ...state, activeId };
}

export function activePreset(state: DashboardState): Preset {
  const preset =
    state.presets.find((p) => p.id === state.activeId) ?? state.presets[0];
  if (!preset) throw new Error("Dashboard state has no presets");
  return preset;
}

export function replacePreset(
  state: DashboardState,
  preset: Preset,
): DashboardState {
  return {
    ...state,
    presets: state.presets.map((p) => (p.id === preset.id ? preset : p)),
  };
}

export function addPreset(
  state: DashboardState,
  name: string,
  from?: Preset,
): DashboardState {
  const preset: Preset = {
    id: newId("preset"),
    name,
    widgets: (from?.widgets ?? []).map((widget) => ({
      ...widget,
      id: newId("w"),
    })),
  };
  return { ...state, activeId: preset.id, presets: [...state.presets, preset] };
}

/** Removes a preset; the last one can never be deleted. */
export function removePreset(state: DashboardState, id: string): DashboardState {
  if (state.presets.length <= 1) return state;
  const presets = state.presets.filter((p) => p.id !== id);
  return {
    ...state,
    presets,
    activeId:
      state.activeId === id ? (presets[0]?.id ?? state.activeId) : state.activeId,
  };
}

export function moveWidget(
  widgets: WidgetConfig[],
  index: number,
  delta: -1 | 1,
): WidgetConfig[] {
  const target = index + delta;
  if (target < 0 || target >= widgets.length) return widgets;
  const next = [...widgets];
  const [moved] = next.splice(index, 1);
  if (!moved) return widgets;
  next.splice(target, 0, moved);
  return next;
}

/** Async persistence seam. Server stores can reject stale writes; the
 * dashboard keeps working with local storage when they are unavailable. */
export interface DashboardStore {
  read: () => Promise<unknown>;
  write: (state: DashboardState) => Promise<void>;
}

export function createLocalStorageStore(key: string): DashboardStore {
  return {
    async read() {
      try {
        const raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    },
    async write(state) {
      try {
        window.localStorage.setItem(key, JSON.stringify(state));
      } catch {
        // Private mode or a full quota: the layout still works for the
        // session, it just is not remembered.
      }
    },
  };
}
